// write by cursor
// 生成时间: 2025-07-29 16:45:55
// 核心请求管理器

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiRequestConfig, ErrorHandlerTools } from '../types/request';
import { SimpleErrorChain } from './error-chain';
import { EndpointManager } from './endpoint-manager';
import { TokenRefreshManager, TokenManagerConfig } from './token-manager';
import {
  createCustomErrorHandler,
  createBusinessErrorHandler,
  createHttpStatusErrorHandler
} from './default-handlers';
import { GS_USER_AGENT, GS_USER_REGION } from '../config/request';
/**
 * 请求管理器配置 - write by cursor
 */
export interface RequestManagerConfig {
  timeout?: number;
  defaultDomain: string;
  disableGlobalErrorHandle?: boolean; // 全局禁用错误处理
  useOriginalError?: boolean; // 是否使用原始错误
  useUserRegion?: boolean; // 是否在 header 中添加 gs-user-region
  tokenConfig: TokenManagerConfig;
  errorCodeMap?: Record<number, string>;
}

/**
 * 请求管理器类 - write by cursor
 */
export class RequestManager {
  private axios: AxiosInstance;
  private endpointManager: EndpointManager;
  private tokenRefreshManager: TokenRefreshManager;
  private globalErrorChain: SimpleErrorChain;
  private disableGlobalErrorHandle: boolean; // 全局错误处理开关
  private useOriginalError: boolean; // 是否使用原始错误

  constructor(config: RequestManagerConfig) {
    // write by cursor - 构建 headers，根据配置决定是否添加 gs-user-region
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'gs-user-agent': GS_USER_AGENT,
    };
    
    // write by cursor - 如果配置了 useUserRegion 为 true，才添加 gs-user-region header
    if (config.useUserRegion) {
      headers['gs-user-region'] = GS_USER_REGION;
    }

    this.axios = axios.create({
      timeout: config.timeout || 60000,
      headers,
    });

    this.endpointManager = new EndpointManager(config.defaultDomain);
    this.tokenRefreshManager = new TokenRefreshManager(config.tokenConfig);
    this.globalErrorChain = new SimpleErrorChain();
    this.disableGlobalErrorHandle = config.disableGlobalErrorHandle || false;
    // 是否使用原始错误, 没有值时为true
    this.useOriginalError = config.useOriginalError ?? true;

    this.initializeErrorChain(config.errorCodeMap || {});
    this.setupInterceptors();
  }

  /**
   * 初始化错误处理链 - write by cursor
   */
  private initializeErrorChain(errorCodeMap: Record<number, string>): void {
    this.globalErrorChain
      .use(createCustomErrorHandler())
      .use(createBusinessErrorHandler(errorCodeMap))
      .use(createHttpStatusErrorHandler());
  }

  /**
   * 设置拦截器 - write by cursor
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.axios.interceptors.request.use(
      (config) => {
        // 从config中提取endpoint信息
        const apiConfig = config as ApiRequestConfig;
        let endpoint = apiConfig.endpoint;
        
        // 构建完整URL
        if (config.url) {
          config.url = this.endpointManager.buildUrl(config.url, endpoint);
        }
        
        // 清理自定义配置，避免传递给axios
        delete apiConfig.endpoint;
        
        // 添加Token
        const token = this.tokenRefreshManager.getAccessToken();
        // 如果httpNoToken为true，则不携带token
        if (token && config.headers && !apiConfig.httpNoToken) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // 响应拦截器
    this.axios.interceptors.response.use(
      async (response: AxiosResponse) => {
        const config = response.config as ApiRequestConfig;
        const responseData = response.data as any;
        // 检查业务错误 - write by cursor
        if (responseData && typeof responseData === 'object' && 'code' in responseData) {
          if (responseData?.code !== 0 && responseData?.code !== 200 && responseData?.code !== null) {
            // 创建业务错误对象
            const businessError = new Error(responseData?.message || responseData?.msg || 'Business error');
            (businessError as any).code = responseData?.code;
            (businessError as any).businessError = true;
            (businessError as any).response = response;

            // 错误处理逻辑 - write by cursor
            // 1. 如果单次请求明确跳过错误处理，直接返回原始数据
            if (config?.skipErrorHandle) {
              return responseData;
            }
            
            // 2. 如果全局禁用且单次请求未启用错误处理，直接返回原始返回数据
            if (this.disableGlobalErrorHandle && !config?.enableErrorHandle) {
              return responseData;
            }
            
            // 3. 如果全局启用或单次请求启用错误处理，使用错误处理链
            const handled = await this.globalErrorChain.handle(businessError, config);
            if (handled) {
              // 错误已被处理，返回 null 或原始数据（业务决定）
              return null;
            }
            
            // 未被处理，抛出错误
            throw businessError;
          }
          
          // code === 0，返回数据
          return responseData
        }
        
        // 非标准响应格式，直接返回
        return responseData;
      },
      async (axiosError: AxiosError) => {
        const originalRequest = axiosError.config as ApiRequestConfig & { _retry?: boolean };
        // 确定是否使用原始错误：优先使用单个请求配置，其次使用全局配置
        const shouldUseOriginalError = originalRequest?.useOriginalError ?? this.useOriginalError;
        const error = shouldUseOriginalError ? axiosError : axiosError.response;
        // 处理401错误 - Token过期 - write by cursor
        if (axiosError.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // 直接等待Token刷新Promise，所有并发请求都会等待同一个Promise - write by cursor
            const newToken = await this.tokenRefreshManager.refreshToken();
            
            // 重新发送原请求 - write by cursor
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        // HTTP错误处理逻辑 - write by cursor
        // 1. 如果单次请求明确跳过错误处理，直接抛出
        if (originalRequest?.skipErrorHandle) {
          return Promise.reject(error);
        }
        
        // 2. 如果全局禁用且单次请求未启用错误处理，直接抛出
        if (this.disableGlobalErrorHandle && !originalRequest?.enableErrorHandle) {
          return Promise.reject(error);
        }

        // 3. 如果全局启用或单次请求启用错误处理，使用错误处理链
        const handled = await this.globalErrorChain.handle(error, originalRequest);
        if (!handled) {
          // 错误未被处理，记录日志并抛出
          return Promise.reject(error);
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * 获取错误处理工具 - write by cursor
   */
  getErrorHandler(): ErrorHandlerTools {
    return {
      use: (handler) => {
        this.globalErrorChain.use(handler);
        return this.getErrorHandler();
      },
      
      forBusinessCode: (code, handler) => {
        return this.getErrorHandler().use((error, config) => {
          if (error.businessError && error.code === code) {
            handler(error, config);
            return true;
          }
          return false;
        });
      },
      
      forHttpStatus: (status, handler) => {
        return this.getErrorHandler().use((error, config) => {
          if (error.response?.status === status) {
            handler(error, config);
            return true;
          }
          return false;
        });
      }
    };
  }

  /**
   * 获取 endpoint 管理器 - write by cursor
   */
  getEndpointManager(): EndpointManager {
    return this.endpointManager;
  }

  /**
   * 通用请求方法 - write by cursor
   */
  async request<T = any>(config: ApiRequestConfig): Promise<T> {
    const response = await this.axios(config);
    return response as T;
  }

  /**
   * GET 请求 - write by cursor
   */
  get<T = any>(url: string, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  /**
   * POST 请求 - write by cursor
   */
  post<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  /**
   * PUT 请求 - write by cursor
   */
  put<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  /**
   * DELETE 请求 - write by cursor
   */
  delete<T = any>(url: string, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  /**
   * PATCH 请求 - write by cursor
   */
  patch<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  /**
   * 销毁方法，清理资源 - write by cursor
   */
  destroy(): void {
    this.tokenRefreshManager.destroy();
    this.globalErrorChain.clear();
  }
} 